import React, { useState } from 'react'
import * as S from '@app/components/molecules/EvaluationsList/styled'
import { InLineSpinner } from '@app/evaluation/components/Spinner'
import ListState, { LIST_SPINNER_SIZE } from '@app/components/atoms/ListState'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import DateTimeView from '@app/components/atoms/DateTimeView'
import TwoButtonModal from '@app/components/organisms/TwoButtonModal'
import { navigate } from '@app/navigation'
import {
  LOAD_ERROR_TEXT,
  useManualSlopeTables,
} from '../hooks/useManualSlopeTables'
import {
  createManualSlopeTable,
  deleteManualSlopeTable,
  SavedSlopeTable,
} from '../manualSlopeTableService'
import { useSnackbar } from '@app/snackbar'
import DownloadManualSlopeTableModal from './DownloadManualSlopeTableModal'
import { DropDownMenu } from '@app/ui/dropdown-menu/DropDownMenu'
import { DropDownMenuItem } from '@app/ui/dropdown-menu/DropDownMenu.styles'
import { FaCopy, FaTrash } from 'react-icons/fa'
import GenderIcon from '@app/components/atoms/GenderIcon'
import { formatHoleRange } from '../holeRange'

type Props = {
  clubId: string
  clubName: string
}

const ManualSlopeTableList: React.FC<Props> = ({ clubId, clubName }) => {
  const { tables, loading, error, retry } = useManualSlopeTables(clubId)
  const [selectedId, setSelectedId] = useState<string>('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [emailTable, setEmailTable] = useState<SavedSlopeTable | null>(null)
  const [openSnackbar] = useSnackbar()

  // Must come before the empty state: telling a club they have no tables when
  // we simply could not read them would be worse than saying nothing
  if (error) {
    return (
      <ListState>
        <span>{LOAD_ERROR_TEXT}</span>
        <RoundedButton title="Try again" onPress={retry} />
      </ListState>
    )
  }

  if (loading) {
    return (
      <ListState $center>
        <InLineSpinner size={LIST_SPINNER_SIZE} />
      </ListState>
    )
  }

  if (!tables.length) {
    return <ListState>No saved slope tables</ListState>
  }

  const handleDelete = async (id: string) => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await deleteManualSlopeTable(clubId, id)
      openSnackbar('Slope table deleted')
    } catch (err) {
      openSnackbar('Failed to delete slope table')
      console.error(err)
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const handleCreateRevision = async (table: SavedSlopeTable) => {
    try {
      const newId = await createManualSlopeTable(clubId, {
        tees: table.tees,
        par: table.par,
        courseName: table.courseName,
        clubName: table.clubName,
        clubId: table.clubId,
        isDraft: true,
        gender: table.gender,
        ratingDate: table.ratingDate,
        ratingName: table.ratingName,
        startHole: table.startHole,
        endHole: table.endHole,
      })
      navigate('CreateSlopeTable', {
        clubName,
        clubId,
        slopeTableId: newId,
      })
    } catch (err) {
      openSnackbar('Failed to create revision')
      console.error(err)
    }
  }

  return (
    <S.TableContainer>
      <S.Table>
        <thead>
          <S.TableRowHead>
            <S.TableHeadCol style={{ width: '20%' }}>Name</S.TableHeadCol>
            <S.TableHeadCol style={{ width: '15%' }}>
              Rating Date
            </S.TableHeadCol>
            <S.TableHeadCol style={{ width: '10%' }}>Holes</S.TableHeadCol>
            <S.TableHeadCol style={{ width: '5%' }}>Par</S.TableHeadCol>
            <S.TableHeadCol style={{ width: '10%' }}>Draft</S.TableHeadCol>
            <S.TableHeadCol style={{ width: '20%' }}>Tees</S.TableHeadCol>
            <S.TableHeadCol style={{ width: 'auto', marginLeft: 'auto' }} />
          </S.TableRowHead>
        </thead>
        <tbody>
          {tables.map((table) => (
            <S.TableRow key={table.id}>
              <S.TableCol>
                {table.ratingName || table.courseName || '-'}
              </S.TableCol>
              <S.TableCol>
                <DateTimeView dateString={table.ratingDate} showTime={false} />
              </S.TableCol>
              <S.TableCol>
                {formatHoleRange(table.startHole, table.endHole) || '-'}
              </S.TableCol>
              <S.TableCol>
                <S.ClubHoleCount style={{ margin: 0 }}>
                  {table.par}
                </S.ClubHoleCount>
              </S.TableCol>
              <S.TableCol>{table.isDraft ? 'Yes' : 'No'}</S.TableCol>
              <S.TableCol>
                <div
                  style={{
                    display: 'flex',
                    flexFlow: 'row wrap',
                    gap: 8,
                  }}
                >
                  {table.tees?.map((tee, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        gap: 4,
                        whiteSpace: 'nowrap',
                        flex: '1 0 auto',
                      }}
                    >
                      <GenderIcon
                        genderKey={table.gender === 'M' ? '1' : '2'}
                      />
                      {tee.title}
                    </div>
                  ))}
                </div>
              </S.TableCol>
              <S.TableCol>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <DropDownMenu
                    isMenu
                    width={180}
                    relative
                    xAdj={-160}
                    yAdj={10}
                    useShadow={false}
                  >
                    <DropDownMenuItem
                      onPress={() => handleCreateRevision(table)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <FaCopy size={14} /> Create copy
                      </div>
                    </DropDownMenuItem>
                    <DropDownMenuItem
                      onPress={() => {
                        setSelectedId(table.id!)
                        setShowDeleteModal(true)
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: '#e74c3c',
                        }}
                      >
                        <FaTrash size={14} /> Delete
                      </div>
                    </DropDownMenuItem>
                  </DropDownMenu>
                  <RoundedButton
                    title="VIEW/EDIT"
                    onPress={() =>
                      navigate('CreateSlopeTable', {
                        clubName,
                        clubId,
                        slopeTableId: table.id,
                      })
                    }
                  />
                  <RoundedButton
                    title="DOWNLOAD"
                    isDefault
                    onPress={() => setEmailTable(table)}
                  />
                </div>
              </S.TableCol>
            </S.TableRow>
          ))}
        </tbody>

        <TwoButtonModal
          isVisible={showDeleteModal}
          title="Are you sure you want to delete this slope table?"
          description="This will permanently remove the saved slope table."
          onPressRight={async () => await handleDelete(selectedId)}
          onPressLeft={() => setShowDeleteModal(false)}
          rightButtonText="Delete"
          leftButtonText="Cancel"
          destructive
        />
      </S.Table>

      <DownloadManualSlopeTableModal
        isVisible={!!emailTable}
        table={emailTable}
        onClose={() => setEmailTable(null)}
      />
    </S.TableContainer>
  )
}

export default ManualSlopeTableList
